import { EnumUtils } from "../../Global/Base/Utils/EnumUtils";
import { GenericControl } from "../UI.BaseControls/GenericControl";
import { EventHelper } from "../UI.BaseControls/Helpers/EventHelper";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { FileEditProgressStatus, FileEditStatus, FileMenuBase, FileMenuEdit, FileMenuEmpty, FileMenuNonEdit, FileMenuType, MenuItemFile } from "../UI/UI/FileMenu";
import { SignatureCssClass } from "../UI/UI/Helpers/CryptoInfo";
import { InlineBtnWithLoaderBuilder } from "../UI/UI/XGControls/Frames/ColumnTemplates/Cells/InlineBtnWithLoaderBuilder";
import { SimpleFileEditElement } from "./SimpleFileEditElement";

export interface FileEditElement {
    addEventListener(type: "cancelRequest", listener: (ev: CustomEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "signatureInfoRequest", listener: (ev: CustomEvent) => any, useCapture?: boolean): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;

    registerEventListener(type: "cancelRequest", listener: (ev: CustomEvent) => any, useCapture?: boolean): void;
    registerEventListener(type: "signatureInfoRequest", listener: (ev: CustomEvent) => any, useCapture?: boolean): void;
    registerEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;
}

/**
 * K simplefileeditelementu přidává menu
 */
export class FileEditElement extends SimpleFileEditElement {
    private _signatureElement: HTMLElement;
    private _signatureElementLoader: InlineBtnWithLoaderBuilder;
    private _rightTextElement: HTMLElement;
    private _uploadWithSigning: boolean; //flag jestli soubor podepsat před uploadem
    private _fileMenu: FileMenuBase;
    private _enableFileMenuDownload: boolean;
    private _enableFileMenuShowVersions: boolean;
    private _enableFileMenuDownloadArchiveActions: boolean;
    private _enableFileMenuCopyUrl: boolean;
    private _failCancelBtn: HTMLButtonElement;
    //#region Properties

    public set enableFileMenuDownload(v: boolean) {
        this._enableFileMenuDownload = v;
    }

    public set enableFileMenuShowVersions(v: boolean) {
        this._enableFileMenuShowVersions = v;
    }

    public set enableFileMenuDownloadArchiveActions(v: boolean) {
        this._enableFileMenuDownloadArchiveActions = v;
    }

    public set enableFileMenuCopyUrl(v: boolean) {
        this._enableFileMenuCopyUrl = v;
    }

    public get fileMenu(): FileMenuBase {
        return this._fileMenu;
    }
    /**
     * @internal
     */
    public get signatureElementLoader(): InlineBtnWithLoaderBuilder {
        return this._signatureElementLoader;
    }

    /**
     * @internal
    */
    public get signatureElement(): HTMLElement {
        return this._signatureElement;
    }

    /**
     * Nastavuje, zda je možné vytvářet digitální podpisy dokumentu
     * @internal
     */
    public updateSigningEnabled(signingEnabled: boolean, signingEnabledIfEmpty: boolean = signingEnabled): void {
        this._rightTextElement.classList.toggle("signing-enabled", signingEnabled);
        // při změně je potřeba skrýt související položky v menu
        if (this._fileMenu)
            this._fileMenu.updateSigningEnabled(signingEnabled, signingEnabledIfEmpty);
    }

    //#endregion Properties

    /**
     * Spustí dialog pro otevření okna
     * @param withSigning flag, jestli se má soubor podepsat
     */
    protected override _triggerOpenFile(withSigning: boolean = false): void {
        this._uploadWithSigning = withSigning;
        super._triggerOpenFile();
    }

    /*public fillSignatureInfoMenu(signatureInfoMenu: DropDownMenu, event: MouseEvent) {
        this._signatureInfoMenu = signatureInfoMenu;
        this._signatureInfoMenu.Show(this as HTMLElement, event);
    }*/

    public override changeProgressStatus(type: FileEditProgressStatus, value?: string | number): void {
        super.changeProgressStatus(type, value);
        if (type === FileEditProgressStatus.progressValue) {
            this._rightTextElement.textContent = value + "%";
            if (value == 100) {
                //this.classList.add("Finishing");
                this._rightTextElement.textContent = "99%";
            }
        } else if (type === FileEditProgressStatus.fail) {
            this._rightTextElement.textContent = "";
        } else if (type === FileEditProgressStatus.complete) {
            this._rightTextElement.textContent = "";
        }
    }

    public override setStatus(status: FileEditStatus): boolean {
        if (!super.setStatus(status))
            return false;

        if (this._fileMenu)
            this._fileMenu.setStatus(status);

        if (this.status === FileEditStatus.empty || this.status === FileEditStatus.download) {
            this._rightTextElement.textContent = "";
        } else if (this.status === FileEditStatus.uploading) {
            this._rightTextElement.textContent = "0%";
        }

        return true;
    }

    public updateSignatureButton(classes: SignatureCssClass[]): void {
        //odstraní staré
        EnumUtils.getEnumAsArray(SignatureCssClass).forEach(className => this._signatureElement.classList.remove(className));

        //Přidá nové
        if (classes) {
            classes.forEach((className) => this._signatureElement.classList.add(className));
        }
    }

    public updateSignatureButtonTooltip(tooltip: string): void {
        this._signatureElement.setAttribute("title", tooltip);
    }

    constructor(_self?: any) {
        super();
    }

    protected override _constructor(setStatus: boolean = false): void {
        super._constructor(setStatus);

        this.classList.add("FileEditElement");
        this._failCancelBtn = createElement({ type: "button", classes: "cancel" }) as HTMLButtonElement;
        this._failBtnBar.appendChild(this._failCancelBtn);

        this._signatureElement = createElement({ type: "div", classes: "file-edit-element__signature valuepicker signature", value: "" }) as HTMLElement;
        this._signatureElementLoader = new InlineBtnWithLoaderBuilder(createElement({ type: "div" }) as HTMLElement);
        const signatureElementContent = createElement(); //dočasný element, bude nahrazen loaderem, který se vytváří asynchronně

        this._rightTextElement = createElement({ type: "div", classes: "file-edit-element__menu-progress valuepicker", value: "0%" }) as HTMLElement;
        this._innerContent.insertBefore(this._signatureElement, this._innerContent.lastChild);
        this._innerContent.insertBefore(this._rightTextElement, this._innerContent.lastChild);
        const a = this._innerContent.insertBefore(signatureElementContent, this._innerContent.lastChild);

        this.updateSigningEnabled(false);
        this.setStatus(FileEditStatus.empty);
        this.enabled = false;

        this._signatureElementLoader.build().then(e => {
            e.classList.add("valuepicker", "signature-loader");
            this._innerContent.replaceChild(e, a);
        });
    }

    /**
     * Otevře nebo zavře file menu a zaregistruje jeho události
     * @param fileMenuType Je typ menu (edit, nonEdit, empty)
     * @param startElement Uvodní element, ze kterého se vypočítává zobrazovaná poloha, pokud je null, bere se to z události
     * @param event Udalost, která pžedchází zobrazení menu
     */
    private _fileMenuShow(fileMenuType: FileMenuType, startElement: HTMLElement, event: Event): void {
        // Jestli je menu otevřené, tak ho zavřeme
        if (this._fileMenu && this._fileMenu.isOpened) {
            this._fileMenu.close(false, event);
            return;
        }
        // Jestli menu nebylo doteď vytvořené nebo je jiného typu, tak ho vytvoříme
        if (!this._fileMenu || this._fileMenu.type != fileMenuType) {
            switch (fileMenuType) {
                case FileMenuType.edit:
                    this._fileMenu = new FileMenuEdit();
                    break;
                case FileMenuType.nonEdit:
                    this._fileMenu = new FileMenuNonEdit();
                    break;
                case FileMenuType.empty:
                    this._fileMenu = new FileMenuEmpty();
                    break;
            }
            this._fileMenu.setMenuItemEnabled(this._enableFileMenuDownload, MenuItemFile.download);
            this._fileMenu.setMenuItemEnabled(this._enableFileMenuDownloadArchiveActions, MenuItemFile.downloadArchiveVersion);
            this._fileMenu.setMenuItemEnabled(this._enableFileMenuShowVersions, MenuItemFile.showVersions);
            this._fileMenu.setMenuItemEnabled(this._enableFileMenuCopyUrl, MenuItemFile.copyUrl);

            this._fileMenu.registerEventListener("triggerOpenFile", (event: CustomEvent) => {
                this._triggerOpenFile(event.detail ? event.detail.withSigning : false);
            });
            this.callEvent("showFileMenu"); // Zavoláme událost showFileMenu, která zaregistruje události pracující s položkami menu. Tato událost se zavolá pouze při prvním otevření menu.
        }
        this._fileMenu.show(startElement, event);
    }

    protected override connectedCallbackSpecial(): void {
        GenericControl.registerEventListener(this._failCancelBtn, "click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            this._files = [];
            this.setStatus(FileEditStatus.empty);
            this.callEvent("cancelRequest");
        });
        this.registerEventListener("click", (e: MouseEvent) => {
            if (e.ctrlKey || e.metaKey)
                return;
            EventHelper.stopPropagation(e);
            if (this.status === FileEditStatus.download || this.status === FileEditStatus.unavailable) {
                this._fileMenuShow(FileMenuType.edit, this as HTMLElement, e);
            } else if (this.status === FileEditStatus.empty) {
                if (!this.enabled)
                    return;
                this._triggerOpenFile();
            }
        });
        GenericControl.registerEventListener(this._signatureElement, "click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            this.callEvent("signatureInfoRequest", { event: e });
        });
        GenericControl.registerEventListener(this._rightTextElement, "click", (e: MouseEvent) => {
            // kliknutí na "tři tečky" mimo editaci - otevře malé menu
            // v editaci to už obsluhuje click handler na this
            //v reřimu empty při editaci umožní zobrazit dodatečné možnosti uploadu
            if (this.status === FileEditStatus.empty && this.enabled) {
                this._fileMenuShow(FileMenuType.empty, this as HTMLElement, e);
                return;
            } else if ((this.status !== FileEditStatus.download && this.status !== FileEditStatus.unavailable) || this.enabled)
                return;

            this._fileMenuShow(FileMenuType.nonEdit, this as HTMLElement, e);
        });
        GenericControl.registerEventListener(this._failReloadBtn, "click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            this.changeProgressStatus(FileEditProgressStatus.progressValue, 0);
            this.callEvent("uploadRequest", { files: this._files, retry: true, sign: this._uploadWithSigning });
        });

        GenericControl.registerEventListener(this._fileElement, "change", async (e) => {
            e.stopPropagation();
            if (!(await this._handleFiles(this._fileElement.files)))
                return;
            this.callEvent("uploadRequest", { files: this._files, sign: this._uploadWithSigning });
        });
    }

    public setInnerElementID(id: string): void {
        this._fileElement.id = id;
    }
    protected override disconnectedCallback(): void {
        super.disconnectedCallback();
        GenericControl.unregisterEventListener(this._failCancelBtn);
        GenericControl.unregisterEventListener(this._signatureElement);
        GenericControl.unregisterEventListener(this._rightTextElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

registerCustomElement("tsc-FileEdit", FileEditElement);
